import { cn } from '@amore/ui/cn'
import { Avatar } from '@amore/ui/components/avatar'
import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { ArrowDown, Check, CircleAlert, Minus, RotateCcw, Sparkles, UserX } from 'lucide-react'
import type { ReactNode } from 'react'
import { formatDate, plural, relativeDue } from '@/lib/format'
import { priorityLabels } from '@/lib/labels'
import type { Task, WeddingData } from '@/mock/types'
import { type Column, DataTable, type TableGroup } from '@/patterns/data-table'
import { useToast } from '@/shell/toaster'
import { automaticNotice, isLate, isPriority, isUpcoming, taskSource } from './model'

const count = (value: number) => `${value} ${plural(value, 'úkol', 'úkoly', 'úkolů')}`

/** The list: collapsible "Prioritní", "Ostatní" and "Hotové" (collapsed) when unfiltered, flat otherwise. */
export function TaskTable({
  tasks,
  grouped,
  columns,
  onOpen,
  rowAction,
  empty,
}: {
  tasks: Task[]
  grouped: boolean
  columns: Column<Task>[]
  onOpen: (task: Task) => void
  rowAction: (task: Task) => ReactNode
  empty: ReactNode
}) {
  const common = {
    columns,
    getRowId: (task: Task) => task.id,
    onRowClick: onOpen,
    rowActions: rowAction,
    rowMuted: (task: Task) => task.done,
    empty,
    'aria-label': 'Seznam úkolů',
  }
  if (!grouped || tasks.length === 0) return <DataTable {...common} rows={tasks} />

  const priority = tasks.filter(isPriority)
  const other = tasks.filter((task) => !task.done && !isPriority(task))
  const done = tasks.filter((task) => task.done)
  const groups: TableGroup<Task>[] = []
  if (priority.length > 0)
    groups.push({
      id: 'priority',
      label: 'Prioritní',
      meta: count(priority.length),
      rows: priority,
      collapsible: true,
    })
  if (other.length > 0)
    groups.push({
      id: 'other',
      label: 'Ostatní',
      meta: count(other.length),
      rows: other,
      collapsible: true,
    })
  if (done.length > 0)
    groups.push({
      id: 'done',
      label: 'Hotové',
      meta: count(done.length),
      rows: done,
      collapsible: true,
      defaultCollapsed: true,
    })
  // One open group and nothing done: a lone "Ostatní" header adds nothing.
  if (groups.length === 1 && done.length === 0) return <DataTable {...common} rows={tasks} />
  return <DataTable {...common} groups={groups} />
}

export function taskColumns(data: WeddingData): Column<Task>[] {
  return [
    {
      key: 'priority',
      header: '',
      width: '1.5rem',
      mobile: 'hide',
      cell: (task) => <PriorityIcon task={task} />,
    },
    {
      key: 'title',
      header: 'Úkol',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: (task) => {
        const source = taskSource(task, data)
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <span className={cn('font-medium', task.done && 'line-through')}>{task.title}</span>
            {source || task.labels.length > 0 ? (
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-normal text-muted-foreground">
                {source ? <span>{source.line}</span> : null}
                {task.labels.map((label) => (
                  <Badge key={label} tone="accent" className="py-0.5">
                    {label}
                  </Badge>
                ))}
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'due',
      header: 'Termín',
      width: '11rem',
      cell: (task) => <DueCell task={task} />,
    },
    {
      key: 'assignee',
      header: 'Odpovědná osoba',
      width: '12rem',
      cell: (task) => {
        const member = data.members.find((item) => item.id === task.assigneeId)
        return member ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="hidden md:block">
              <Avatar alt="" size="sm" fallback={member.initials} className="size-7" />
            </span>
            <span className="truncate">{member.name}</span>
          </span>
        ) : (
          <span className="flex items-center gap-2 text-muted-foreground">
            <UserX className="hidden size-4 md:block" aria-hidden="true" /> Bez přiřazení
          </span>
        )
      },
    },
  ]
}

function PriorityIcon({ task }: { task: Task }) {
  const label = `${priorityLabels[task.priority]} priorita`
  const Icon = task.priority === 'high' ? CircleAlert : task.priority === 'low' ? ArrowDown : Minus
  return (
    <span title={label} className="flex">
      <Icon
        aria-hidden="true"
        className={cn(
          'size-4',
          task.priority === 'high' ? 'text-danger-foreground' : 'text-muted-foreground',
        )}
      />
      <span className="sr-only">{label}</span>
    </span>
  )
}

function DueCell({ task }: { task: Task }) {
  if (!task.dueDate) return <span className="text-muted-foreground">Bez termínu</span>
  const late = isLate(task)
  return (
    <span className="flex flex-col items-end gap-0.5 md:items-start">
      <span
        className={cn(
          late && 'font-semibold text-danger-foreground',
          !late && isUpcoming(task) && 'font-medium',
        )}
      >
        {task.done ? formatDate(task.dueDate) : relativeDue(task.dueDate)}
      </span>
      {task.done ? null : (
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {formatDate(task.dueDate)}
          {late ? (
            <Badge tone="danger" className="py-0.5">
              Po termínu
            </Badge>
          ) : null}
        </span>
      )}
    </span>
  )
}

export function CompleteToggle({
  task,
  canEdit,
  onChange,
}: {
  task: Task
  canEdit: boolean
  onChange: (done: boolean) => void
}) {
  const { showToast } = useToast()
  if (task.automatic && !task.done)
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            appearance="outline"
            tone="success"
            size="icon"
            disabled={!canEdit}
            aria-label="Stav se mění automaticky"
            onClick={() =>
              showToast({
                title: 'Stav se mění automaticky',
                description: automaticNotice(task) ?? undefined,
              })
            }
            className="size-9"
          >
            <Sparkles className="size-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Stav se mění automaticky</TooltipContent>
      </Tooltip>
    )
  return (
    <Button
      appearance={task.done ? 'outline' : 'solid'}
      tone={task.done ? 'neutral' : 'success'}
      size="icon"
      disabled={!canEdit}
      aria-label={task.done ? 'Obnovit úkol' : 'Splnit úkol'}
      title={task.done ? 'Obnovit úkol' : 'Splnit úkol'}
      onClick={() => onChange(!task.done)}
      className="size-9"
    >
      {task.done ? (
        <RotateCcw className="size-4" aria-hidden="true" />
      ) : (
        <Check className="size-4" aria-hidden="true" />
      )}
    </Button>
  )
}
